import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Shield, type LucideIcon } from 'lucide-react';
import type { DemoCategory } from '../../data/demoGraphs';
import { DemoScenarioCard } from './DemoScenarioCard';
import { GraphLegend } from './GraphLegend';
import { DashboardIcon } from './DashboardIcon';
import {
    DASHBOARD_SHOW_ALL_DEMOS_KEY,
    listDemoScenarios,
    splitDemoCatalog,
    type DemoCatalogScenario,
} from './demoCatalog';
import {
    dashboardFilterChipActive,
    dashboardFilterChipInactive,
    dashboardSectionTitle,
} from './dashboardStyles';
import {
    ALL_CATEGORY_CONFIG,
    CATEGORY_ICON_MAP,
    type DashboardIconTone,
} from './dashboardIconConfig';

type ScenarioCategory = DemoCategory;

type ScenarioCategoryEntry = {
    key: ScenarioCategory | 'all';
    label: string;
    icon: LucideIcon;
    tone: DashboardIconTone;
    description: string;
};

const scenarioCategories: ScenarioCategoryEntry[] = [
    { key: 'all', ...ALL_CATEGORY_CONFIG },
    ...(Object.entries(CATEGORY_ICON_MAP) as [ScenarioCategory, typeof CATEGORY_ICON_MAP[ScenarioCategory]][]).map(
        ([key, config]) => ({ key, ...config }),
    ),
];

type Props = {
    onScenarioClick: (scenario: DemoCatalogScenario) => void;
};

export const DemoCatalog: React.FC<Props> = ({ onScenarioClick }) => {
    const demoScenarios = useMemo(() => listDemoScenarios(), []);
    const [scenarioFilter, setScenarioFilter] = useState<ScenarioCategory | 'all'>('all');
    const [showAllDemos, setShowAllDemos] = useState(
        () => typeof window !== 'undefined' && localStorage.getItem(DASHBOARD_SHOW_ALL_DEMOS_KEY) === '1',
    );

    const cycleScenarioFilter = React.useCallback((delta: number) => {
        const keys = scenarioCategories.map((c) => c.key);
        setScenarioFilter((prev) => {
            const idx = keys.indexOf(prev);
            const next = (idx + delta + keys.length) % keys.length;
            return keys[next];
        });
    }, []);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            e.preventDefault();
            cycleScenarioFilter(e.key === 'ArrowRight' ? 1 : -1);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [cycleScenarioFilter]);

    const groupedScenarios = useMemo(() => {
        const categoryOrder: ScenarioCategory[] = ['live-wallets', 'threats', 'crime-patterns', 'analytics', 'tutorials'];
        if (scenarioFilter !== 'all') {
            const cat = scenarioCategories.find((c) => c.key === scenarioFilter)!;
            return [{ ...cat, scenarios: demoScenarios.filter((s) => s.category === scenarioFilter) }];
        }
        return categoryOrder.map((cat) => {
            const meta = scenarioCategories.find((c) => c.key === cat)!;
            return { ...meta, scenarios: demoScenarios.filter((s) => s.category === cat) };
        });
    }, [demoScenarios, scenarioFilter]);

    return (
        <div id="dashboard-demos" data-tour="dashboard-demos">
            <div className="mb-6">
                <GraphLegend defaultExpanded />
            </div>

            <div>
                <h2 className={`${dashboardSectionTitle} mb-2 flex items-center gap-2 tracking-tight`}>
                    <DashboardIcon variant="section" tone="neutral" icon={Shield} />
                    {showAllDemos ? 'All demo scenarios' : 'Featured demo scenarios'}
                    <span className="text-xs font-normal text-muted-foreground ml-1">({demoScenarios.length})</span>
                </h2>
                <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
                    {showAllDemos
                        ? 'Browse all training graphs by category. These are not live cases.'
                        : 'Start Here and Featured cards stay visible. Extra tutorials stay collapsed so the page stays scannable.'}
                </p>
            </div>

            <div className="flex items-start gap-2 mb-6">
                <button
                    onClick={() => cycleScenarioFilter(-1)}
                    aria-label="Previous category"
                    title="Previous category (←)"
                    className="shrink-0 mt-0.5 p-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex flex-wrap gap-2 flex-1">
                    {scenarioCategories.map((cat) => {
                        const count = cat.key === 'all' ? demoScenarios.length : demoScenarios.filter((s) => s.category === cat.key).length;
                        const isActive = scenarioFilter === cat.key;
                        return (
                            <button
                                key={cat.key}
                                onClick={() => setScenarioFilter(cat.key)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    isActive ? dashboardFilterChipActive : dashboardFilterChipInactive
                                }`}
                            >
                                <DashboardIcon
                                    variant="inline"
                                    tone={isActive ? 'primary' : cat.tone}
                                    icon={cat.icon}
                                    className={isActive ? 'text-primary-foreground' : undefined}
                                />
                                {cat.label}
                                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                    isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-secondary text-muted-foreground'
                                }`}>{count}</span>
                            </button>
                        );
                    })}
                </div>
                <button
                    onClick={() => cycleScenarioFilter(1)}
                    aria-label="Next category"
                    title="Next category (→)"
                    className="shrink-0 mt-0.5 p-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-8">
                {groupedScenarios.map((group) => {
                    const { visible, hiddenCount } = splitDemoCatalog(group.scenarios, showAllDemos);
                    if (visible.length === 0) return null;
                    return (
                        <div key={group.key}>
                            {scenarioFilter === 'all' && (
                                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border/50">
                                    <DashboardIcon variant="inline" tone={group.tone} icon={group.icon} />
                                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{group.label}</h3>
                                    <span className="text-xs text-muted-foreground">({showAllDemos ? group.scenarios.length : `${visible.length} of ${group.scenarios.length}`})</span>
                                    <span className="text-xs text-muted-foreground/60 ml-2">{group.description}</span>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {visible.map((scenario) => (
                                    <DemoScenarioCard
                                        key={scenario.id}
                                        scenario={scenario}
                                        onClick={() => onScenarioClick(scenario)}
                                    />
                                ))}
                            </div>
                            {!showAllDemos && hiddenCount > 0 && scenarioFilter !== 'all' && (
                                <p className="text-xs text-muted-foreground mt-2">
                                    {hiddenCount} more in this category.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
            {(() => {
                const inFilter = demoScenarios.filter((s) =>
                    scenarioFilter === 'all' ? true : s.category === scenarioFilter,
                );
                const { hiddenCount } = splitDemoCatalog(inFilter, false);
                if (hiddenCount <= 0) return null;
                return (
                    <div className="mt-6 flex justify-center">
                        <button
                            type="button"
                            data-testid="dashboard-toggle-all-demos"
                            onClick={() => {
                                const next = !showAllDemos;
                                setShowAllDemos(next);
                                try {
                                    if (next) localStorage.setItem(DASHBOARD_SHOW_ALL_DEMOS_KEY, '1');
                                    else localStorage.removeItem(DASHBOARD_SHOW_ALL_DEMOS_KEY);
                                } catch { /* private mode */ }
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                            {showAllDemos ? 'Show featured only' : `Show all ${inFilter.length} demos`}
                        </button>
                    </div>
                );
            })()}
        </div>
    );
};
